import { useShallow } from 'zustand/react/shallow';
import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Alert,
  Loading,
  handleDLProductClickEvent,
  globals,
  Icon,
  handleAlgoliaCartEvent,
  MIN_PRODUCT_QUANTITY_MESSAGE,
} from 'common-ui';
import { useActionAddToCart, useActionBatchAddToCart, useCartStore } from 'common-state';
import { selectCartDetails } from 'common-state/src/stores/cartStore';
import type { AxiosErrorResponse } from 'tsconfig/types';
import useProductData from '../../hooks/useProductData';
import { isSubscriptionCaseItem } from '../../utils/subscriptionCase';

export type CartProps = {
  itemCode: string;
  qty: string;
  buttonText?: string;
  sendEvent?: any;
  hit?: any;
  isDisable?: boolean;
  method?: string;
  cartItems?: any;
  fontWeight?: string;
  isUnlimitedWithMember?: boolean;
  upperCase?: boolean;
  closeModal?: () => void;
  buttonStyle?: 'primary' | 'secondary' | 'tertiary' | 'transparent';
  isMiniCart?: boolean;
  isSubscribeAndSave?: string;
  showSuccessAlert?: boolean;
  PDPSubscribeAndSaveView?: string;
  handleSKUMissing?: boolean;
  onAddClick?: () => void;
};

type ButtonContentProps = {
  loadingState: boolean;
  isUK: boolean;
  isUnlimitedWithMember?: boolean;
  resolvedButtonText: string;
  shortButtonText: string;
};

const ButtonContent = ({
  loadingState,
  isUK,
  isUnlimitedWithMember,
  resolvedButtonText,
  shortButtonText,
}: ButtonContentProps): React.ReactElement => {
  if (loadingState) {
    return (
      <>
        <span className="loadIcon">
          <Loading />
        </span>
        <span className={`addingText${isUK ? ' btn-text-full' : ''}`}>Adding</span>
      </>
    );
  }
  return (
    <span>
      {isUnlimitedWithMember && (
        <span className="unlimited-icon">
          <Icon kind="unlimited" size="xs" />
          &nbsp;&nbsp;
        </span>
      )}
      {isUK ? (
        <>
          <span className="btn-text-full">{resolvedButtonText}</span>
          <span className="btn-text-short" style={{ display: 'none' }}>
            {shortButtonText}
          </span>
        </>
      ) : (
        <span>{resolvedButtonText}</span>
      )}
    </span>
  );
};

export const CartButton = (props: CartProps): React.ReactElement => {
  const {
    itemCode,
    qty,
    buttonText = 'ADD TO BASKET',
    isDisable,
    method,
    cartItems,
    fontWeight,
    isUnlimitedWithMember,
    upperCase,
    closeModal,
    buttonStyle = 'primary',
    isMiniCart,
    isSubscribeAndSave = null,
    showSuccessAlert = false,
    hit,
    PDPSubscribeAndSaveView = null,
    handleSKUMissing,
    onAddClick,
  } = props;

  const { itemsCount } = useCartStore(useShallow(selectCartDetails));
  const {
    cartError: { hasCartError, cartErrorMessage },
  } = useCartStore(state => state.cart);

  const { fetchProductData } = useProductData();

  const [apiCallStatus, setApiCallStatus] = useState('idle');
  const [apiCallMessage, setApiCallMessage] = useState('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const addItemToCart = useActionAddToCart();
  const batchAddItemToCart = useActionBatchAddToCart();
  const loadingState = apiCallStatus === 'loading';

  const isUK = globals.country === 'uk';
  const isUKAndSS = isUK && PDPSubscribeAndSaveView;
  const convertQtyToInt = Number.parseInt(qty, 10);
  const resolvedButtonText = upperCase ? buttonText.toUpperCase() : buttonText;
  const isSubscriptionCase = isSubscriptionCaseItem(hit);
  const shortTextIfNotSubscription = upperCase ? buttonText.split(' ')[0].toUpperCase() : buttonText.split(' ')[0];
  const shortButtonText = isSubscriptionCase ? resolvedButtonText : shortTextIfNotSubscription;

  const handleApiError = (error: AxiosErrorResponse): string =>
    error?.response?.data?.errorResponse?.message ||
    error?.response?.data?.message ||
    error.message ||
    'There is an error adding to cart. Please try again.';

  const setErrorWithTimeout = (message: string, timeout = 3000) => {
    // Clear any existing timeout to prevent multiple alerts stacking
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setApiCallStatus('error');
    setApiCallMessage(message);
    timeoutRef.current = setTimeout(() => {
      setApiCallStatus('');
      setApiCallMessage('');
    }, timeout);
  };

  const setSuccessWithTimeout = (message: string, timeout = 3000) => {
    // Clear any existing timeout to prevent multiple alerts stacking
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setApiCallStatus('success');
    setApiCallMessage(message);
    timeoutRef.current = setTimeout(() => {
      setApiCallStatus('');
      setApiCallMessage('');
    }, timeout);
  };

  const validateCartInput = (cartItemCode: string): string | null => {
    if (hasCartError) {
      return cartErrorMessage;
    }
    if (Number.isNaN(convertQtyToInt) || convertQtyToInt < 1 || !Number.isFinite(convertQtyToInt)) {
      return MIN_PRODUCT_QUANTITY_MESSAGE;
    }
    if (cartItemCode === '') {
      return 'Item number is required';
    }
    return null;
  };

  const fireAnalyticsEvents = (cartItemCode: string, isInitialCart: boolean) => {
    handleAlgoliaCartEvent(cartItemCode, convertQtyToInt, hit, isUnlimitedWithMember);

    const eventType = isInitialCart ? 'InitiateCart' : 'addToCart';
    const location = isMiniCart ? 'miniCart' : undefined;
    handleDLProductClickEvent(eventType, hit, convertQtyToInt, location, cartItemCode);

    if (isUKAndSS) {
      handleDLProductClickEvent('SubscribeSaveProduct', hit, convertQtyToInt, PDPSubscribeAndSaveView, itemCode);
    }
  };

  const fireAnalyticsWithFetch = async (cartItemCode: string, isInitialCart: boolean, productData: any) => {
    const eventType = isInitialCart ? 'InitiateCart' : 'addToCart';
    const location = isMiniCart ? 'miniCart' : undefined;
    handleDLProductClickEvent(eventType, productData, convertQtyToInt, location, cartItemCode);
  };

  const handleAddToCartClick = async (code: string) => {
    const validationError = validateCartInput(code);
    if (validationError) {
      setErrorWithTimeout(validationError);
      return;
    }

    let cartItemCode = code;
    // If the item code from props is not part of the hit's skuItemCodes,
    // default to the first sku item code for the cart request.
    // This is to handle cases where the displayed item code may differ from the actual SKU item codes associated with the product.
    if (handleSKUMissing && code && hit && hit.skuItemCodes?.length > 0 && !hit.skuItemCodes.includes(code)) {
      [cartItemCode] = hit.skuItemCodes;
    }

    setApiCallStatus('loading'); // Set loading status while the API call is in progress

    try {
      // Add multiple items to the cart by including an additional object in the array for AWS cart API
      const cartItemsToAdd = [{ itemCode: cartItemCode, quantity: convertQtyToInt }];

      await addItemToCart(cartItemsToAdd);

      const isInitialCart = itemsCount <= 0;

      if (hit) {
        fireAnalyticsEvents(cartItemCode, isInitialCart);
      } else {
        const productDataForAnalytics = await fetchProductData(itemCode);
        if (productDataForAnalytics) {
          fireAnalyticsWithFetch(cartItemCode, isInitialCart, productDataForAnalytics);
        }
      }

      setApiCallStatus('success');

      if (showSuccessAlert) {
        setSuccessWithTimeout(`Item ${cartItemCode} added to your cart`);
      }

      if (closeModal) {
        closeModal();
      }
    } catch (error) {
      const errorMessage = error ? handleApiError(error as AxiosErrorResponse) : 'An error occurred.';
      setErrorWithTimeout(errorMessage, 5000);
    }
  };

  const fireBatchAnalyticsEvents = (isInitialCart: boolean) => {
    if (!hit) {
      return;
    }

    const eventType = isInitialCart ? 'InitiateCart' : 'addToCart';
    const location = isMiniCart ? 'miniCart' : undefined;
    handleDLProductClickEvent(eventType, hit, convertQtyToInt, location);

    if (isUKAndSS) {
      handleDLProductClickEvent('SubscribeSaveProduct', hit, convertQtyToInt, PDPSubscribeAndSaveView, itemCode);
    }

    if (isSubscribeAndSave) {
      handleDLProductClickEvent(
        'SubscribeSaveCart',
        hit,
        convertQtyToInt,
        `subscribeSave_${isSubscribeAndSave}`,
        itemCode,
      );
    }
  };

  const handleBatchAddToCartClick = async (cartAddedItems: any) => {
    setApiCallStatus('loading'); // Set loading status while the API call is in progress

    try {
      if (globals?.useNewCartAPI) {
        await addItemToCart(cartAddedItems.cartItems);
      } else {
        await batchAddItemToCart(cartAddedItems);
      }

      // Adobe DataLayer
      // If hit is not passed in as a prop, then we don't fire the cart events as the product data is required to be passed as part of the event
      const isInitialCart = itemsCount <= 0;
      fireBatchAnalyticsEvents(isInitialCart);

      setApiCallStatus('success'); // Set success status after a successful API call

      // Close the modal if it exists, after adding items to the cart
      if (closeModal) {
        closeModal();
      }
    } catch (error) {
      const errorMessage = error ? handleApiError(error as AxiosErrorResponse) : 'An error occurred.';
      setErrorWithTimeout(errorMessage, 7500);
    }
  };

  const shouldShowAlert =
    apiCallMessage !== '' && (apiCallStatus === 'error' || (apiCallStatus === 'success' && showSuccessAlert));

  return (
    <>
      <div className="cart-button">
        <Button
          data-testid="add-to-cart-button"
          className={`${loadingState ? 'loading-add-to-cart-btn' : 'grid-addto-cart-btn'}`}
          // className="loadIcon"
          kind={isUnlimitedWithMember ? 'primaryUnlimited' : buttonStyle}
          style={{ fontWeight }}
          onClick={() => {
            onAddClick?.();
            if (method === 'batch') {
              handleBatchAddToCartClick(cartItems);
            } else {
              handleAddToCartClick(itemCode);
            }
          }}
          disabled={isDisable || loadingState}
        >
          <ButtonContent
            loadingState={loadingState}
            isUK={isUK}
            isUnlimitedWithMember={isUnlimitedWithMember}
            resolvedButtonText={resolvedButtonText}
            shortButtonText={shortButtonText}
          />
        </Button>
      </div>
      {shouldShowAlert && (
        <div>
          <Alert type={apiCallStatus}>{apiCallMessage}</Alert>
        </div>
      )}
    </>
  );
};
